import { usePage } from "@inertiajs/react"
import type { Page as InertiaPage } from "@inertiajs/core"
import React from "react"

import type { Host, Readable, VisitRequest } from "./host"
import { inertiaHost } from "./inertia-host"
import type { RequestBody, SerialisableBody, TransientData } from "./types"

// The React binding of the host contract: hooks over its readables with
// useSyncExternalStore, and the host's own link. A runtime provides a host
// with HostProvider (the embedded Driver does); a page under none reads the
// root host over Inertia.

const HostContext = React.createContext<Host | null>(null)

export function HostProvider({
  host,
  children,
}: {
  host: Host
  children: React.ReactNode
}) {
  return <HostContext value={host}>{children}</HostContext>
}

// Inertia's page, or null outside its App: a Driver rendered on its own, as
// its spec does. Inertia exposes no optional read of its context, only a
// hook that throws without one, so the throw is what says there is none.
function useInertiaPage(): InertiaPage | null {
  try {
    return usePage()
  } catch {
    return null
  }
}

export function useHost(): Host {
  const provided = React.use(HostContext)
  const page = useInertiaPage()
  if (provided !== null) return provided
  if (page === null) {
    throw new Error("[page-rendering] no runtime provides this page a host")
  }
  return inertiaHost(page)
}

// The host enclosing a runtime being mounted, or null when it is the first:
// the embedded Driver reads its session from it and hands it the pages its
// scope declines.
export function useEnclosingHost(): Host | null {
  const provided = React.use(HostContext)
  const page = useInertiaPage()
  if (provided !== null) return provided
  return page === null ? null : inertiaHost(page)
}

export function useReadable<T>(readable: Readable<T>): T {
  return React.useSyncExternalStore(
    readable.subscribe,
    readable.get,
    readable.get,
  )
}

export const useSession = () => useReadable(useHost().session)

export const useEntry = () => useReadable(useHost().entry)

export const useVisit = () => useReadable(useHost().visit)

// The entry's props, as the visit that committed it delivered them. A page
// narrows them to its own props type.
export function useProps<T extends object = SerialisableBody>(): T {
  return useVisit().props as T
}

// The visit's transient data: true for this visit only, set by the request
// that performed a change and read by the page it redirected to. A page
// narrows it to what it expects.
export function useTransient<T extends TransientData>(): T {
  return useVisit().transient as T
}

// Whether the layer has a visit in flight, so a form can hold its submit
// action while the request it started is unresolved.
export const usePendingVisit = () => useVisit().pending

export const useNavigate = () => useHost().navigate

// The enclosing layer's sink for a page a child declines.
export const useReceive = () => useHost().receive

export const useFeatureToggle = (featureToggle: string) =>
  useSession().featureToggles.includes(featureToggle)

export const usePathParams = () => useEntry().pathParams

export const usePathParam = (key: string) => usePathParams()[key]

// Reports whether a source on the entry holds unsaved work; withdrawn when
// the source unmounts.
export function useLeaveGuard(dirty: boolean) {
  const { guard } = useHost()
  const [source] = React.useState(() => Symbol("leave-guard-source"))

  React.useEffect(() => {
    guard.report(source, dirty)
    return () => guard.report(source, false)
  }, [guard, source, dirty])
}

// Whether any source on the entry holds unsaved work.
export const useLeaveGuarded = () => useReadable(useHost().guard.dirty)

// Whether a click on a link is the layer's to handle: a plain left click on
// something not being edited. A modified click (a new tab or window) is the
// browser's and leaves the layer where it is.
function shouldIntercept(event: React.MouseEvent<HTMLElement>) {
  const isLink = event.currentTarget.tagName.toLowerCase() === "a"
  return !(
    (event.target instanceof HTMLElement && event.target.isContentEditable) ||
    event.defaultPrevented ||
    (isLink && event.altKey) ||
    (isLink && event.ctrlKey) ||
    (isLink && event.metaKey) ||
    (isLink && event.shiftKey) ||
    (isLink && event.button !== 0)
  )
}

// The href as the anchor carries it: resolved against the document, kept
// in the form it was written (absolute, relative, or with a host), with a
// GET's data merged into its query string so a modified click lands on the
// same page.
function hrefWithQuery(href: string, data: RequestBody | undefined) {
  const hasData = data !== undefined && Object.keys(data).length > 0
  const hasHost = /^([a-z][a-z0-9+.-]*:)?\/\/[^/]/i.test(href)
  const hasAbsolutePath = hasHost || href.startsWith("/") || href === ""
  const hasRelativePath =
    !hasAbsolutePath && !href.startsWith("#") && !href.startsWith("?")
  const hasDotPrefix = /^[.]{1,2}([/]|$)/.test(href)
  const hasSearch = href.includes("?") || hasData
  const hasHash = href.includes("#")

  const url = new URL(href, window.location.href)
  if (hasData) {
    for (const [key, value] of Object.entries(data)) {
      if (value === undefined || value === null) continue
      if (Array.isArray(value)) {
        url.searchParams.delete(`${key}[]`)
        for (const item of value)
          url.searchParams.append(`${key}[]`, String(item))
      } else {
        url.searchParams.set(key, String(value))
      }
    }
  }

  return [
    hasHost ? `${url.protocol}//${url.host}` : "",
    hasAbsolutePath ? url.pathname : "",
    hasRelativePath ? url.pathname.substring(hasDotPrefix ? 0 : 1) : "",
    hasSearch ? url.search : "",
    hasHash ? url.hash : "",
  ].join("")
}

export type LinkProps = Omit<
  React.ComponentPropsWithRef<"a">,
  "href" | "onClick"
> &
  Pick<VisitRequest, "method" | "data" | "replace" | "preserveScroll"> & {
    href: string
    // What renders the link: an anchor by default, a button for a method
    // other than GET, or a component given the same props.
    as?: React.ElementType
    onClick?: (event: React.MouseEvent<HTMLElement>) => void
  }

// A link that is the host's visit, in the layer it is rendered in.
export function Link({
  as = "a",
  href,
  method = "get",
  data,
  replace,
  preserveScroll,
  onClick,
  ...props
}: LinkProps) {
  const { navigate } = useHost()
  const url = hrefWithQuery(href, method === "get" ? data : undefined)
  const body = method === "get" ? undefined : data
  const element: React.ElementType =
    typeof as === "string" && as.toLowerCase() === "a" && method !== "get"
      ? "button"
      : as
  const elementProps =
    element === "button"
      ? { type: "button" }
      : element === "a" || typeof element !== "string"
        ? { href: url }
        : {}

  return React.createElement(element, {
    ...props,
    ...elementProps,
    onClick: (event: React.MouseEvent<HTMLElement>) => {
      onClick?.(event)
      if (!shouldIntercept(event)) return
      event.preventDefault()
      navigate({ href: url, method, data: body, replace, preserveScroll })
    },
  })
}
